import { useState, useEffect, useRef } from "react";
import API from "../services/api";
import { setToken, isLoggedIn } from "../utils/auth";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import axios from "axios";
import { Dropdown, Form, Button } from "react-bootstrap";
import {
  Phone, ArrowRight, ArrowLeft, MapPin,
  KeyRound, ShieldCheck, User, Hotel, MessageCircle,
} from "lucide-react";
import "../assets/auth.css"
import "../assets/login.css"


const Login = () => {
  const [mobile, setMobile] = useState("");
  const [step, setStep] = useState(1);
  const [otpId, setOtpId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [country_code, setCountryCode] = useState("+974");
  const [countries, setCountries] = useState([]);

  const [selectedCountry, setSelectedCountry] = useState(null);
  const [countrySearch, setCountrySearch] = useState("");
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);

  const [error, setError] = useState(null);
  const inputRefs = useRef([]);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res = await axios.get("https://realestate.alallamtech.com/demo/api-company/countries/list");
        setCountries(res.data);

        // Preselect Qatar (matches default country_code state)
        const defaultCountry = res.data.find((c) => `+${c.phonecode}` === "+974");
        if (defaultCountry) setSelectedCountry(defaultCountry);
      } catch (error) {
        console.error("Error fetching countries", error);
      }
    };

    fetchCountries();
  }, []);

  // Redirect if already logged in
  useEffect(() => {
    if (isLoggedIn()) {
      navigate("/dashboard");
    }
  }, [navigate]);

  const validateMobile = (phone) => {
    // Basic validation - adjust according to your needs
    const phoneRegex = /^\d{7,15}$/;
    return phoneRegex.test(phone);
  };

  const handleSendOtp = async () => {
    if (!mobile.trim()) {
      return Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "Please enter mobile number",
        showConfirmButton: false,
        timer: 2000,
      });
    }

    if (!validateMobile(mobile)) {
      return Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "Please enter a valid mobile number",
        showConfirmButton: false,
        timer: 2000,
      });
    }

    if (!country_code) {
      return Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "Please select country code",
        showConfirmButton: false,
        timer: 2000,
      });
    }

    try {
      setLoading(true);
      const res = await API.post("/login", {
        phone_no: mobile.trim(),
        country_code: country_code,
      });

      if (res.data.statuscode === 200) {
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: res.data.message || "OTP sent successfully!",
          showConfirmButton: false,
          timer: 2500,
        });

        setOtpId(res.data.hotel_group_master_id);
        setStep(2);
        setError(null);
      } else {
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "error",
          title: res.data.message || "Failed to send OTP",
          showConfirmButton: false,
          timer: 3000,
        });
      }
    } catch (error) {
      console.error("Send OTP Error:", error);
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "Failed to send OTP. Please try again.",
        showConfirmButton: false,
        timer: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  /* ── OTP box handlers (uncontrolled, matches Otp.jsx) ── */
  const handleInput = (e, idx) => {
    const val = e.target.value;
    if (val.length > 1) e.target.value = val.slice(-1);
    if (e.target.value) {
      e.target.classList.add("filled");
      if (idx < 5) inputRefs.current[idx + 1].focus();
    } else {
      e.target.classList.remove("filled");
    }
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === "Backspace" && !e.target.value && idx > 0) {
      inputRefs.current[idx - 1].focus();
      inputRefs.current[idx - 1].value = "";
      inputRefs.current[idx - 1].classList.remove("filled");
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = (e.clipboardData || window.clipboardData)
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);
    pasted.split("").forEach((ch, i) => {
      if (inputRefs.current[i]) {
        inputRefs.current[i].value = ch;
        inputRefs.current[i].classList.add("filled");
      }
    });
    const next = Math.min(pasted.length, 5);
    inputRefs.current[next].focus();
  };

  const handleVerifyOtp = async () => {
    const otpValue = inputRefs.current.map((el) => el.value).join("");

    if (otpValue.length < 4) {
      setError("Please enter all digits.");
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "OTP must be at least 4 digits",
        showConfirmButton: false,
        timer: 2000,
      });
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await API.post("/login/otp", {
        hotel_group_master_id: otpId,
        otp: otpValue,
      });

      if (res.data.status === 200) {
        setToken(res.data.token);
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: "Login successful!",
          showConfirmButton: false,
          timer: 1500,
        });
        navigate("/dashboard");
      } else {
        setError(res.data.message || "Invalid OTP");
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "error",
          title: res.data.message || "Invalid OTP",
          showConfirmButton: false,
          timer: 2500,
        });
      }
    } catch (error) {
      console.error("Verify OTP Error:", error);
      setError("Invalid OTP or session expired");
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "Invalid OTP or session expired",
        showConfirmButton: false,
        timer: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!mobile || !country_code) return;

    try {
      setResendLoading(true);
      const res = await API.post("/login", {
        phone_no: mobile.trim(),
        country_code: country_code,
      });

      if (res.data.statuscode === 200) {
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: "OTP resent successfully",
          showConfirmButton: false,
          timer: 2000,
        });
        setOtpId(res.data.hotel_group_master_id);
      } else {
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "error",
          title: res.data.message || "Failed to resend OTP",
          showConfirmButton: false,
          timer: 2500,
        });
      }
    } catch (error) {
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "Failed to resend OTP",
        showConfirmButton: false,
        timer: 3000,
      });
    } finally {
      setResendLoading(false);
    }
  };

  const handleBackStep = () => {
    setStep(1);
    setError(null);
  };

  const filteredCountries = countries.filter((c) =>
    c.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
    `+${c.phonecode}`.includes(countrySearch)
  );

  /* ══════════════════ Step 2: exact Otp.jsx screen ══════════════════ */
  if (step === 2) {
    return (
      <div className="otp-page">
        <div
          onClick={handleBackStep}
          className="d-flex align-items-center justify-content-center text-white"
          style={{
            height: "40px",
            width: "40px",
            backgroundColor: "#25a244",
            borderRadius: "50%",
            cursor: "pointer",
            position: "absolute",
            top: "20px",
            left: "20px",
            zIndex: 3,
          }}
        >
          <ArrowLeft size={20} />
        </div>

        <svg className="otp-skyline" viewBox="0 0 900 220" fill="#25a244" xmlns="http://www.w3.org/2000/svg">
          <rect x="10" y="70" width="42" height="150" /><rect x="14" y="50" width="34" height="20" />
          <rect x="62" y="35" width="52" height="185" /><rect x="66" y="15" width="44" height="20" />
          <rect x="124" y="90" width="36" height="130" />
          <rect x="170" y="25" width="62" height="195" /><rect x="174" y="5" width="54" height="20" />
          <rect x="242" y="60" width="46" height="160" />
          <rect x="298" y="15" width="68" height="205" /><rect x="302" y="-5" width="60" height="20" />
          <rect x="376" y="75" width="40" height="145" />
          <rect x="426" y="30" width="56" height="190" /><rect x="430" y="10" width="48" height="20" />
          <rect x="492" y="95" width="34" height="125" />
          <rect x="536" y="40" width="58" height="180" />
          <rect x="604" y="70" width="42" height="150" />
          <rect x="656" y="20" width="64" height="200" /><rect x="660" y="0" width="56" height="20" />
          <rect x="730" y="80" width="38" height="140" />
          <rect x="778" y="50" width="52" height="170" />
          <rect x="840" y="85" width="36" height="135" />
        </svg>

        <div className="otp-card">
          <div className="otp-header">
            <h2 className="otp-heading">Enter verification code</h2>
            <p className="otp-subtext">
              A code was sent to<br /><strong>{country_code} {mobile}</strong>
            </p>
          </div>

          <div className="wa-row">
            <div className="wa-track" />
            <div className="wa-inner">
              <span className="wa-inner-text">OTP sent successfully ✓</span>
            </div>
            <div className="wa-circle">
              <div className="wa-ring">
                <div className="wa-core">
                  <MessageCircle size={26} stroke="#fff" strokeWidth={2.2} />
                </div>
              </div>
            </div>
          </div>

          {error && <div className="auth-error-msg">{error}</div>}

          <div className="otp-inputs-wrap">
            {[0, 1, 2, 3, 4, 5].map((idx) => (
              <input
                key={idx}
                className="otp-box"
                type="number"
                min="0"
                max="9"
                maxLength={1}
                inputMode="numeric"
                aria-label={`OTP digit ${idx + 1}`}
                ref={(el) => (inputRefs.current[idx] = el)}
                onInput={(e) => handleInput(e, idx)}
                onKeyDown={(e) => handleKeyDown(e, idx)}
                onPaste={handlePaste}
                disabled={loading}
              />
            ))}
          </div>

          <button
            className="vbtn-main"
            onClick={handleVerifyOtp}
            disabled={loading}
          >
            <ShieldCheck size={18} stroke="#fff" strokeWidth={2.3} />
            {loading ? "Verifying..." : "Verify & Continue"}
          </button>

          {/* <p className="text-center mt-3 mb-0" style={{ fontSize: "0.85rem" }}>
            Didn't receive the code?{" "}
            <span
              style={{ color: "#25a244", cursor: "pointer", fontWeight: 600 }}
              onClick={handleResendOtp}
            >
              {resendLoading ? "Resending..." : "Resend OTP"}
            </span>
          </p> */}
        </div>
      </div>
    );
  }

  /* ══════════════════ Step 1: mobile + country entry ══════════════════ */
  return (
    <main className="estate-access-screen">
      <header className="estate-access-header">
        <div className="estate-access-status">
          <ShieldCheck size={17} />
          <span>Hotels Portal</span>
        </div>
      </header>

      <section className="estate-access-main">
        <div className="estate-bg-plan">
          <span className="plan-line plan-line-one"></span>
          <span className="plan-line plan-line-two"></span>
          <span className="plan-line plan-line-three"></span>
          <span className="plan-line plan-line-four"></span>
        </div>

        <div className="estate-floating-item estate-item-one">
          <Hotel size={18} />
          <div>
            <strong>60+</strong>
            <span>Hotels</span>
          </div>
        </div>

        <div className="estate-floating-item estate-item-two">
          <MapPin size={18} />
          <div>
            <strong>110+</strong>
            <span>Locations</span>
          </div>
        </div>

        <div className="estate-floating-item estate-item-three">
          <KeyRound size={18} />
          <div>
            <strong>Secure</strong>
            <span>OTP Login</span>
          </div>
        </div>

        <div className="estate-login-box">
          <div className="estate-access-logo mb-2">
            <img
              src="https://demo.alallamcleaning.com/uploads/malaz-no-bg.png"
              alt="logo"
            />
          </div>

          <div className="estate-login-title">
            <h1>Welcome 👋</h1>
            <p>Sign in to manage your hotel listings and bookings.</p>
          </div>

          <div className="estate-modern-form">
            <div className="estate-modern-field">
              <label htmlFor="country">Country Code</label>
              <Dropdown className="estate-modern-input"
                autoClose="outside"
                show={showCountryDropdown}
                onToggle={(isOpen) => setShowCountryDropdown(isOpen)}
              >
                <Dropdown.Toggle
                  variant="outline-light"
                  style={{
                    color: "#212529",
                    width: "100%",
                    textAlign: "left",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {selectedCountry
                    ? `${selectedCountry.name} (+${selectedCountry.phonecode})`
                    : "Select Country"}
                </Dropdown.Toggle>

                <Dropdown.Menu style={{ width: "100%", padding: "12px" }}>
                  <Form.Control
                    type="text"
                    placeholder="Search country..."
                    className="mb-2"
                    value={countrySearch}
                    onChange={(e) => setCountrySearch(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                  />
                  <div style={{ maxHeight: "220px", overflowY: "auto" }}>
                    {filteredCountries.length > 0 ? (
                      filteredCountries.map((country) => (
                        <Dropdown.Item
                          key={country.country_master_id}
                          active={selectedCountry?.country_master_id === country.country_master_id}
                          onClick={() => {
                            setSelectedCountry(country);
                            setCountryCode(`+${country.phonecode}`);
                            setCountrySearch("");
                            setShowCountryDropdown(false);
                          }}
                        >
                          {country.name} (+{country.phonecode})
                        </Dropdown.Item>
                      ))
                    ) : (
                      <p className="text-muted small mb-0 px-2">No countries found</p>
                    )}
                  </div>
                </Dropdown.Menu>
              </Dropdown>
            </div>

            <div className="estate-modern-field">
              <label htmlFor="mobile">Mobile Number</label>
              <div className="estate-modern-input">
                <Phone size={18} />
                <input
                  type="tel"
                  name="mobile"
                  id="mobile"
                  placeholder="Enter mobile number"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value.replace(/[^0-9]/g, ""))}
                  maxLength={15}
                  disabled={loading}
                />
              </div>
            </div>

            <Button
              type="button"
              className="estate-modern-button"
              onClick={handleSendOtp}
              disabled={loading}
            >
              {loading ? "Sending OTP..." : "Send OTP"}
              {!loading && <ArrowRight size={18} />}
            </Button>
          </div>
        </div>
      </section>
    </main>
  );
}

export default Login;